import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, ArrowRight, LogOut } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import PasswordStrength from "@/components/PasswordStrength";
import PasswordInput from "@/components/PasswordInput";

/**
 * Self-serve password change. Reached automatically on first login for any
 * user whose `must_change_password=true` (admin invite or admin-issued reset),
 * or manually from Account Settings.
 */
export default function ChangePassword() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const forced = !!user?.must_change_password;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError("New passwords don't match.");
      return;
    }
    if (next === current) {
      setError("New password must be different from your current/temporary one.");
      return;
    }
    setBusy(true);
    try {
      await http.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Welcome.");
      await refresh();
      // Send them to the right place based on role
      const dest =
        user?.role === "parent" ? "/dashboard/parent"
        : user?.role === "recruiter" ? "/dashboard/recruiter"
        : (user?.role === "super_admin" || user?.role === "tester_admin") ? "/dashboard/admin"
        : "/";
      nav(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Couldn't update password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-12">
      <div className="bento-card max-w-lg w-full p-8 md:p-10" data-testid="change-password-card">
        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-electric/30 bg-electric/10 mb-4">
          <Lock size={11} className="text-electric" />
          <span className="text-[10px] font-display font-bold tracking-widest uppercase text-electric">
            {forced ? "First Login · Set Your Password" : "Account Security"}
          </span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight leading-tight">
          {forced ? <>Create your <span className="text-electric">personal</span> password.</> : <>Change your password.</>}
        </h1>
        {forced && (
          <p className="text-sm text-white/70 mt-4 leading-relaxed">
            Welcome, <strong className="text-white">{user?.name || user?.email}</strong>. Your temporary password was a one-time key to get you in. Set a permanent password you'll remember — it replaces the temp password immediately.
          </p>
        )}

        <form onSubmit={submit} className="space-y-4 mt-6" data-testid="change-password-form">
          <div>
            <label className="label-mono block mb-1.5">{forced ? "Temporary password" : "Current password"}</label>
            <PasswordInput
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="input-dark w-full rounded-md px-4 py-3 text-sm"
              placeholder={forced ? "The temp password your admin sent you" : "Your current password"}
              testId="cp-current"
              autoFocus
              autoComplete={forced ? "one-time-code" : "current-password"}
            />
          </div>

          <div>
            <label className="label-mono block mb-1.5">New password</label>
            <PasswordInput
              required
              minLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              className="input-dark w-full rounded-md px-4 py-3 text-sm"
              placeholder="At least 8 characters · letter + number"
              testId="cp-new"
            />
            <PasswordStrength value={next} />
            <p className="text-[10px] text-white/40 mt-1.5 leading-relaxed">
              Minimum 8 characters with one letter and one number. Use something memorable but not reused from another site.
            </p>
          </div>

          <div>
            <label className="label-mono block mb-1.5">Confirm new password</label>
            <PasswordInput
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="input-dark w-full rounded-md px-4 py-3 text-sm"
              placeholder="Re-type your new password"
              testId="cp-confirm"
            />
          </div>

          {error && (
            <div className="text-sm text-rose-300 bg-rose-400/10 border border-rose-400/25 rounded-md px-3 py-2" data-testid="cp-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full justify-center disabled:opacity-50"
            data-testid="cp-submit"
          >
            <ShieldCheck size={14} /> {busy ? "Updating…" : "Save new password"} <ArrowRight size={12} />
          </button>

          {forced && (
            <button
              type="button"
              onClick={() => logout().then(() => window.location.assign("/login"))}
              className="btn-ghost w-full justify-center text-xs"
              data-testid="cp-logout"
            >
              <LogOut size={12} /> Cancel and log out
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
